import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Body2,
  Button,
  Card,
  CheckboxCircleBlankIcon,
  ConfirmDialog,
  Header3,
  SkeletonLoader,
} from "@activecollab/components";
import { Divider } from "~/components/ModalObjects";
import { Page } from "~/components/Page";
import { StripeFormDialog } from "~/pages/AppsAndIntegrations/Intergations/StripeFormDialog";
import { useIntegrations } from "~/repository";
import { useUpdatePaymentGateway } from "~/repository/usePaymentGateways";
import Angie from "~/utils/angie";

const StripeIntegrationPage = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { data, isFetched, isLoading } = useIntegrations();
  const { mutate: updateStripe } = useUpdatePaymentGateway();
  const stripeIntegration = useMemo(() => {
    if (isFetched) {
      return data?.find((i) => i.short_name === "stripe");
    }
  }, [data, isFetched]);

  const onOpenDialog = useCallback(() => setOpenDialog(true), [setOpenDialog]);
  const onCloseDialog = useCallback(() => setOpenDialog(false), [setOpenDialog]);
  const openDisconnectDialog = useCallback(() => setIsDisconnectDialogOpen(true), []);
  const closeDisconnectDialog = useCallback(() => setIsDisconnectDialogOpen(false), []);

  const disableStripe = useCallback(() => {
    updateStripe(
      {
        credit_card: {
          is_enabled: false,
        },
      },
      { onSettled: () => closeDisconnectDialog() }
    );
  }, [closeDisconnectDialog, updateStripe]);

  return (
    <Page
      title="Add-On - Stripe"
      className="tw-max-w-[850px] tw-pb-4"
    >
      <Page.Header
        title={t("Stripe")}
        className="print:tw-hidden"
        backTitle={t("Apps & Integrations")}
        backUrl="/integrations"
      />
      <Card
        variant="paper-2"
        className="tw-px-8 tw-py-6 tw-h-full"
      >
        <div className="tw-flex tw-flex-1 sm:tw-gap-[60px] tw-gap-8 tw-mb-1 sm:tw-flex-row tw-flex-col-reverse">
          <div className="tw-flex tw-flex-1 tw-flex-col">
            <Header3>{t("Web and Mobile Payments")}</Header3>
            <Body2 className="tw-mt-4">
              {t(
                "Stripe allows you to receive online payments for the invoices you send to your clients. They won't need a Stripe account and they'll be able to pay by credit card."
              )}
            </Body2>
          </div>
          <div className="tw-w-[80px] tw-h-[80px] tw-flex tw-justify-end tw-items-end">
            <img
              src={`${Angie.assets_url}/system/images/integrations/stripe.png`}
              alt="Stripe Integration"
            />
          </div>
        </div>
        <Divider />
        {isLoading ? (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <SkeletonLoader data-testid="loader" />
            <SkeletonLoader />
            <SkeletonLoader />
          </div>
        ) : !stripeIntegration?.is_in_use ? (
          <div className="tw-flex tw-flex-col">
            <div className="tw-flex tw-gap-4 tw-flex-col">
              <Header3>{t("Configure Credit Card Payments")}</Header3>
              <Body2>{t("There aren't any Stripe accounts configured yet.")}</Body2>
              <Button
                className="tw-w-fit"
                onClick={onOpenDialog}
              >
                {t("Configure Stripe")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div className="tw-flex tw-gap-2 tw-items-center">
                <Header3 weight="bold">{t("Stripe Payments Enabled")}</Header3>
                <CheckboxCircleBlankIcon fill="var(--color-sucess-green)" />
              </div>
              <Body2>
                {t("Stripe connected successfully. You can now accept credit card payments from customers worldwide.")}
              </Body2>
            </div>
            <Divider />
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Header3>{t("Disable Credit Card Payments")}</Header3>
              <Body2>
                {t("This will immediately disable credit card payments. Any pending transactions will still process.")}
              </Body2>
            </div>
            <Button
              className="tw-w-fit tw-mt-4"
              onClick={openDisconnectDialog}
            >
              {t("Disconnect Stripe")}
            </Button>
          </>
        )}
        {openDialog ? (
          <StripeFormDialog
            open={openDialog}
            onClose={onCloseDialog}
            integration={stripeIntegration}
          />
        ) : null}
        {isDisconnectDialogOpen ? (
          <ConfirmDialog
            open={isDisconnectDialogOpen}
            onConfirm={disableStripe}
            onCancel={closeDisconnectDialog}
            dialogTitle={t("Disconnect from Stripe")}
            dialogContent={t("Your clients will no longer be able to pay your invoices with credit cards.")}
            confirmBtnText={t("Disconnect")}
            shouldShowCancelButton
          />
        ) : null}
      </Card>
    </Page>
  );
};

export default StripeIntegrationPage;
